<template>
  <div>
    <fieldset class="lib__form__fieldset" v-if="withContactType">
      <legend class="lib__form__legend">{{ $t('common.contact.form.type') }}</legend>
      <contact-type-selector :data-vv-as="$t('common.contact.form.type')" name="contactType" v-model="value.contactType" :lockContactType="lockContactType"></contact-type-selector>
    </fieldset>

    <template v-if="value.contactType === 'freelanceOrCompany' || value.contactType === 'publicAuthority'">
      <fieldset class="lib__form__fieldset">
        <legend class="lib__form__legend">{{ $t('common.contact.form.yourContactInformation') }}</legend>
        <div class="row">
          <div class="col-xs-12 col-md-6">
            <select-box :label="$t('common.contact.form.companyType')" :options="companyTypes" required v-model="value.companyType"></select-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.companyName')" v-model="value.companyName" :required="value.companyType !== 'physicalPerson'"></text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.vatNumber')" v-model="value.vatNumber" vat-number onlyCharAndNumbers :required="isVatRequired" :tooltip="vatTooltip"></text-box>
          </div>
        </div>
      </fieldset>
      <fieldset class="lib__form__fieldset">
        <legend class="lib__form__legend">{{ $t('common.contact.form.contactPerson') }}</legend>

        <div class="row">
          <div class="col-xs-12">
            <civility-selector :data-vv-as="$t('common.contact.form.civility')" name="civility" v-model="value.civility"></civility-selector>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.firstName')" data-cy="contact-form-firstname" required v-model="value.firstName" :min="3" :max="30"></text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.lastName')" data-cy="contact-form-lastname" required v-model="value.lastName" :min="3" :max="30"></text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.email')" data-cy="contact-form-email" email required v-model="value.email"></text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.job')" data-cy="contact-form-job" required v-model="value.job"></text-box>
          </div>
          <div class="col-xs-12 col-md-6" v-if="showBirthdate">
            <date-selector :label="$t('common.contact.form.birthdate')" data-cy="contact-form-birthdate" required v-model="value.birthdate"></date-selector>
          </div>
          <div class="col-xs-12 col-md-6" v-if="showNationalId">
            <masked-text-box
              :label="$t('common.contact.form.nationalid')"
              data-cy="contact-form-nationalid"
              mask="##.##.##-###.##"
              pattern="^\d{2}\.\d{2}\.\d{2}-\d{3}\.\d{2}$"
              :rules="{ 'national-id': true }"
              v-model="value.nationalId"
            ></masked-text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <phone-box :label="$t('common.contact.form.mobile')" :required="!value.phone || value.phone === '+32 '" data-cy="contact-form-mobile" gsm v-model="value.mobile"></phone-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <collapsable-phone-box :addLabel="$t('common.contact.form.addPhone')" :label="$t('common.contact.form.phone')" :required="!value.mobile || value.mobile === '+32 '" data-cy="contact-form-phone" v-model="value.phone"></collapsable-phone-box>
          </div>
        </div>
      </fieldset>
    </template>

    <template v-else>
      <fieldset class="lib__form__fieldset">
        <legend class="lib__form__legend">{{ $t('common.contact.form.civility') }}</legend>
        <civility-selector :data-vv-as="$t('common.contact.form.civility')" name="civility" v-model="value.civility"></civility-selector>
      </fieldset>
      <fieldset class="lib__form__fieldset">
        <legend class="lib__form__legend">{{ $t('common.contact.form.yourContactInformation') }}</legend>

        <div class="row">
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.firstName')" data-cy="contact-form-firstname" required v-model="value.firstName" :min="3" :max="30"></text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.lastName')" data-cy="contact-form-lastname" required v-model="value.lastName" :min="3" :max="30"></text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <text-box :label="$t('common.contact.form.email')" data-cy="contact-form-email" email required v-model="value.email"></text-box>
          </div>
          <div class="col-xs-12 col-md-6" v-if="showBirthdate">
            <date-selector :label="$t('common.contact.form.birthdate')" data-cy="contact-form-birthdate" :required=isBirthDateRequired v-model="value.birthdate"></date-selector>
          </div>
          <div class="col-xs-12 col-md-6" v-if="showNationalId">
            <masked-text-box
              :label="$t('common.contact.form.nationalid')"
              mask="##.##.##-###.##"
              pattern="^\d{2}\.\d{2}\.\d{2}-\d{3}\.\d{2}$"
              :rules="{ 'national-id': true }"
              v-model="value.nationalId"
            ></masked-text-box>
          </div>
          <div class="col-xs-12 col-md-6" v-if='showCustomerRef'>
            <text-box :label="$t('common.contact.form.customerRef')" :max='12' v-model="value.customerRef"></text-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <phone-box :label="$t('common.contact.form.mobile')" :required="!value.phone || value.phone === '+32 '" data-cy="contact-form-mobile" gsm v-model="value.mobile"></phone-box>
          </div>
          <div class="col-xs-12 col-md-6">
            <collapsable-phone-box :addLabel="$t('common.contact.form.addPhone')" :label="$t('common.contact.form.phone')" :required="!value.mobile || value.mobile === '+32 '" data-cy="contact-form-phone" v-model="value.phone"></collapsable-phone-box>
          </div>
        </div>
      </fieldset>
    </template>

    <fieldset class="lib__form__fieldset" v-if="showBankInformation">
      <legend class="lib__form__legend">
        {{ $t('common.contact.bankAccountInformation') }}
        <sup class="lib__tooltips lib__tooltips--large" style="margin-left: 5px">
          <span class="lib__tooltips__trigger">
            <i class="fas fa-info"></i>
          </span>
          <span class="lib__tooltips__data">{{ $t('common.contact.bankAccountInformationMessage') }}</span>
        </sup>
      </legend>

      <div class="row">
        <div class="col-xs-12 col-md-6">
          <text-box :label="$t('common.contact.iban')" iban :rules="{'required': false, 'alpha_num': true}" v-model="value.bankAccountIban"></text-box>
        </div>
        <div class="col-xs-12 col-md-6" v-if="bicVisible">
          <masked-text-box :label="$t('common.contact.bic')" mask="SSSSSSXXXXX" pattern="[A-Z,a-z]{6}[A-Z,a-z,0-9]{5}" required v-model="value.bankAccountBic"></masked-text-box>
        </div>
      </div>
    </fieldset>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { ContactBaseInfo } from '@/models'
import ContactTypeSelector from '@/components/inputs/contact/ContactTypeSelector.vue'
import { CompanyType, PublicAuthorityType } from '@/models/common/contact'

@Component({
  components: {
    ContactTypeSelector,
  },
})
export default class ContactBaseForm extends Vue {
  //#region [Property]
  @Prop({ type: Boolean, required: false, default: true }) public readonly showBirthdate!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly showNationalId!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly showCustomerRef!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly showBankInformation!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly lockContactType!: boolean
  @Prop({ type: Boolean, required: false, default: true }) public readonly isBirthDateRequired!: boolean
  @Prop({ type: Boolean, required: false, default: true }) public readonly withContactType!: boolean
  @Prop({ type: Boolean, required: false, default: true }) public readonly isVatRequired!: boolean
  @Prop({ type: String, required: false}) public readonly vatTooltip!: string
  @Prop({ type: Object, required: true }) public readonly value!: ContactBaseInfo
  //#endregion

  //#region [Computed]
  public get bicVisible(): boolean {
    const bicVisible = (
      this.value.bankAccountIban !== null &&
      this.value.bankAccountIban.length > 1 &&
      !this.value.bankAccountIban.toLowerCase().startsWith('be')
    )

    if (!bicVisible) {
      this.value.bankAccountBic = null
    }
    return bicVisible
  }

  public get companyTypes(): Array<{ text: string; value: string; disable?: boolean }> {
    const list: Array<{ text: string; value: string; disable?: boolean }> = []

    if (this.value.contactType === 'freelanceOrCompany') {
      for (const type of Object.keys(CompanyType)) {
        list.push({ value: type, text: this.$t(`common.contact.companyType.${type}`) as string })
      }
    }

    if (this.value.contactType === 'publicAuthority') {
      for (const type of Object.keys(PublicAuthorityType)) {
        list.push({ value: type, text: this.$t(`common.contact.publicAuthorityType.${type}`) as string })
      }
    }

    return list
  }
  //#endregion

  public mounted(): void {
    if (!this.withContactType) {
      // auto set contact type to individual when contact type choice not available
      this.$emit('input', Object.assign({}, this.value, {contactType: 'individual'}))
    }
  }
}
</script>
